// Product Price — replaces the OE Commerce Core product-detail live-data block
// (price, VAT lines, promo, retail comparison, stock, quantity, add-to-cart,
// wishlist, compare) with a widget that prices the product using the SAME rules
// as the Parts Finder results grid:
//
//   • Price source  : role-based customer price from /api/prices/customer
//                      (resolved via the storefront Session cookie +
//                      commerce_features JWT) when present, else the
//                      Solr-indexed price from /api/parts/:sku. Excl-VAT.
//   • VAT           : incl = excl * 1.15; both lines gated by
//                      showPriceInclVat / showPriceExclVat.
//   • Promo         : reducedFrom > price → struck-through "was" + ON PROMO.
//   • Retail compare: only when role feature showPriceComparison is on →
//                      "Retail: Rxxx incl. VAT" from /api/prices/standard.
//   • Gating        : showPrices (+ "login for pricing" when anonymous),
//                      showStockStatus, allowAddToCart.
//   • Stock         : real-time /api/prices/stock (in/out + qty) wins, else
//                      Solr warehouse_stock / stock_status fallback.
//
// Cart is reimplemented against the native storefront endpoint
// POST /ajax/cart/add-item { Sku, Qty } (the storefront resolves SKU→ProductId
// server-side). Wishlist + Compare reproduce the native markup/attributes so
// the storefront's own handlers bind — there is no documented AJAX endpoint for
// them in the Parts Finder API. The existing branch-stock nodes
// (.product-stock-by-location, the #pboh-branchonhand link) and the payment
// widgets container are PRESERVED (moved into the new block) so sibling widgets
// keep working.
//
// Config (read from the TARGET element, not the script tag — there's one bundle
// tag now):
//   Target (default): [sftarget="live-data"]
//   Override + per-instance config on an optional [data-parts-product-price]:
//     data-target-selector  — CSS selector for the mount to replace
//     data-sku              — explicit SKU
//     data-sku-selector     — element to read the SKU from (data-sku / value)

import { getAppBaseUrl } from '../shared/config.js';
import { getTopDocument, esc } from '../shared/dom.js';
import { readCookie, getSessionRef, loadFeatures, isAnonymous, isBranch } from '../shared/identity.js';
import { apiFetch, apiJson } from '../shared/api.js';
import { formatPriceIncl, formatPriceExcl, workshopLineHtml } from '../shared/format.js';
import { vehicleCartData } from '../shared/vehicle.js';
import { addedToCartToast, addFailedToast } from '../shared/cartToast.js';

// Debug flag — honoured when ?pfdebug=1 is on the PAGE url. (The legacy per-widget
// <script> src check is gone: there's a single bundle tag now, not this widget's.)
var PF_DEBUG = false;
try { PF_DEBUG = /[?&]pfdebug\b/.test(location.search); } catch (e) { /* ignore */ }

// Overrides written by fetchPricesAndStock — mirror ResultsGrid reading from
// priceStore.prices / priceStore.stockBysku on the React side.
var _priceOverrides = {};        // { sku: number }  (excl-VAT)
var _stockOverrides = {};        // { sku: { available, status } }
var _branchListCache = [];       // [{ branchShortCode, branchName, ... }] for branch-aware stock
var _reducedFromOverrides = {};  // { sku: number }  (excl-VAT promo "was")
var _standardPriceOverrides = {};// { sku: number }  (excl-VAT retail/B2C)
var _features = {};

/* ───────────────────────── role features / gating ────────────────────── */

function _loginUrl() {
	try {
		var origin = (window.top && window.top.location && window.top.location.origin) || window.location.origin;
		var loc = (window.top && window.top.location) || window.location;
		// redirectTo must be a relative PATH (path + query), not the absolute URL.
		return origin + '/account/login?redirectTo=' + encodeURIComponent(loc.pathname + loc.search + loc.hash);
	} catch (e) { return '/account/login'; }
}

function _loginPromptHtml() {
	return ''
		+ '<a href="' + esc(_loginUrl()) + '" class="pfpd-login-for-pricing">'
		+ '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" '
		+ 'stroke-linecap="round" stroke-linejoin="round"><path d="M12 11c0-1.1.9-2 2-2h4a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2h4m4 0V7a4 4 0 10-8 0v4"/></svg>'
		+ '<span>Please login for pricing</span>'
		+ '<span style="margin-left:auto;">Sign in &rarr;</span>'
		+ '</a>';
}

/* ──────────────────── price / stock resolution (PF rules) ─────────────── */

// Customer-specific price wins over Solr price — matches PartCard overridePrice.
function _resolvePrice(part) {
	var sku = part.sku || '';
	if (sku && typeof _priceOverrides[sku] === 'number') return _priceOverrides[sku];
	var n = Number(part.price);
	return !n || isNaN(n) ? 0 : n;
}
function _resolveReducedFrom(part) {
	var sku = part.sku || '';
	if (sku && typeof _reducedFromOverrides[sku] === 'number') return _reducedFromOverrides[sku];
	return 0;
}
function _resolveStandardPrice(part) {
	var sku = part.sku || '';
	if (sku && typeof _standardPriceOverrides[sku] === 'number') return _standardPriceOverrides[sku];
	return 0;
}
// Head Office / internal warehouses — not customer branches. Their stock is
// combined INTO the selected branch's availability (same rule set as
// storefront-branch-on-hand.js and storefront-hide-head-office.js).
var _HIDDEN_BRANCHES = ['headoffice', 'hq', 'capedc'];
// (_normBranch is declared later in this file and hoisted.)

// Branch-aware availability: when a branch is selected, "in stock" = the
// selected branch's on-hand + Head Office (+ other hidden warehouses). Returns
// null when we can't compute it (no branch selected / no per-branch data).
function _branchAwareStock(ws, branchList) {
	var sel = _normBranch(_selectedBranch().name);
	if (!sel || !ws || typeof ws !== 'object') return null;
	var nameByCode = {};
	(branchList || []).forEach(function (b) {
		var sc = (b.branchShortCode || b.branchCode || '').trim().toUpperCase();
		var nm = (b.branchName || b.name || '').trim();
		if (sc) nameByCode[sc] = nm;
	});
	function dispName(key) { return nameByCode[key.toUpperCase()] || key; }
	function isHidden(key) {
		return _HIDDEN_BRANCHES.indexOf(_normBranch(key)) !== -1 || _HIDDEN_BRANCHES.indexOf(_normBranch(dispName(key))) !== -1;
	}
	var selQty = 0, hiddenQty = 0;
	Object.keys(ws).forEach(function (key) {
		var q = (ws[key] || 0);
		if (isHidden(key)) { hiddenQty += q; return; }
		if (_normBranch(key) === sel || _normBranch(dispName(key)) === sel) selQty += q;
	});
	var qty = selQty + hiddenQty;
	return { qty: qty, status: qty > 0 ? 'in_stock' : 'out_of_stock' };
}

function _resolveStock(part) {
	var sku = part.sku || '';
	var ws = part.warehouse_stock;
	// Branch selected → availability is the selected branch + Head Office only.
	var branchAware = _branchAwareStock(ws, _branchListCache);
	if (branchAware) return branchAware;
	// No branch selected → current logic.
	// 1. Real-time storefront database stock (from /api/prices/stock) wins.
	if (sku && _stockOverrides[sku]) {
		var s = _stockOverrides[sku];
		return { qty: s.available, status: s.status };
	}
	// 2. Fallback: aggregate Solr warehouse_stock, else stock_status.
	if (!ws || typeof ws !== 'object') {
		return { qty: undefined, status: part.stock_status || 'out_of_stock' };
	}
	var qty = 0;
	for (var k in ws) { if (ws.hasOwnProperty(k)) qty += (ws[k] || 0); }
	return { qty: qty, status: qty > 0 ? 'in_stock' : 'out_of_stock' };
}

/* ───────────────────────────── data fetching ─────────────────────────── */

// Fetch the indexed part (Solr) for the base price + product metadata.
function fetchPart(sku) {
	return apiJson('/api/parts/' + encodeURIComponent(sku), null, null);
}

// Fetch the branch list (name + short code) to resolve warehouse_stock keys
// to display names and identify head office. Proxy first, storefront fallback,
// then page globals — mirrors storefront-branch-on-hand.js.
function fetchBranches() {
	return apiJson('/api/branches', null, null)
		.then(function (data) { if (Array.isArray(data) && data.length > 0) return data; throw new Error('empty'); })
		.catch(function () {
			var origin = '';
			try { origin = (window.top || window).location.origin; } catch (e) {}
			if (!origin) return [];
			return fetch(origin + '/api/branches', { headers: { 'Accept': 'application/json' } })
				.then(function (r) { return r.ok ? r.json() : []; })
				.catch(function () { return []; });
		})
		.then(function (list) {
			if (Array.isArray(list) && list.length > 0) return list;
			// Last resort: storefront page globals.
			var topWin; try { topWin = window.top || window; } catch (e) { topWin = window; }
			var names = ['StoreBranches', 'storeBranches', 'branches', 'Branches', 'BRANCHES', 'storefront_branches'];
			for (var i = 0; i < names.length; i++) {
				var v = topWin[names[i]];
				if (Array.isArray(v) && v.length > 0) return v;
			}
			return [];
		});
}

// Credit context via the shared credit guard. Used to hide per-branch stock
// when the visitor is a gated account user without credit at the selected
// branch. Degrades to null (→ show stock) when credit-guard.js isn't on the
// page, so the widget still works without it.
//
// credit-guard.js and this bundle load in parallel, and this can run before
// the guard has defined window.PFCreditGuard — so wait briefly for it rather
// than giving up immediately (which would leave stock shown for a gated user).
function _pfcgReady() {
	return !!(window.PFCreditGuard && typeof window.PFCreditGuard.getContext === 'function');
}
function fetchCreditContext() {
	if (_pfcgReady()) {
		try { return window.PFCreditGuard.getContext(); } catch (e) { return Promise.resolve(null); }
	}
	return new Promise(function (resolve) {
		var tries = 0;
		(function waitForGuard() {
			if (_pfcgReady()) {
				try { resolve(window.PFCreditGuard.getContext()); } catch (e) { resolve(null); }
				return;
			}
			if (++tries > 30) { resolve(null); return; } // ~3s, then give up (fail open)
			setTimeout(waitForGuard, 100);
		})();
	});
}

// Fetch customer price + standard price + stock — mirrors useCustomerPrices +
// useStandardPrices + useStockStatus invoked by ResultsGrid.
function fetchPricesAndStock(skus) {
	if (!skus || skus.length === 0) return Promise.resolve();
	var base = getAppBaseUrl().replace(/\/$/, '');
	var sessionRef = getSessionRef();
	var jwt = readCookie('commerce_features');
	var qs = '?skus=' + encodeURIComponent(skus.join(','));

	// Customer prices are keyed off the storefront session/features headers — NOT
	// the Bearer API key — so this stays a raw fetch (apiFetch would attach Bearer).
	var pricesHeaders = { 'Accept': 'application/json' };
	if (sessionRef) pricesHeaders['X-Session-Reference'] = sessionRef;
	if (jwt) pricesHeaders['X-Commerce-Features'] = jwt;

	var pricesP = (sessionRef || jwt)
		? fetch(base + '/api/prices/customer' + qs, { method: 'GET', headers: pricesHeaders })
			.then(function (r) { return r.ok ? r.json() : { prices: [] }; })
			.then(function (data) {
				var list = (data && data.prices) ? data.prices : [];
				for (var i = 0; i < list.length; i++) {
					var p = list[i];
					if (p && p.sku && typeof p.price === 'number' && p.price > 0) {
						_priceOverrides[p.sku] = p.price;
						if (typeof p.reducedFrom === 'number' && p.reducedFrom > p.price) {
							_reducedFromOverrides[p.sku] = p.reducedFrom;
						}
					}
				}
			})
			.catch(function () { /* prices are non-critical */ })
		: Promise.resolve();

	// Retail comparison — only when the role has showPriceComparison on.
	var standardP = (_features && _features.showPriceComparison === true)
		? fetch(base + '/api/prices/standard' + qs, { method: 'GET', headers: { 'Accept': 'application/json' } })
			.then(function (r) { return r.ok ? r.json() : { prices: [] }; })
			.then(function (data) {
				var list = (data && data.prices) ? data.prices : [];
				for (var i = 0; i < list.length; i++) {
					var p = list[i];
					if (p && p.sku && typeof p.price === 'number') _standardPriceOverrides[p.sku] = p.price;
				}
			})
			.catch(function () { /* non-critical */ })
		: Promise.resolve();

	var stockP = apiFetch('/api/prices/stock' + qs)
		.then(function (r) { return r.ok ? r.json() : { stock: [] }; })
		.then(function (data) {
			var list = (data && data.stock) ? data.stock : [];
			for (var i = 0; i < list.length; i++) {
				var s = list[i];
				if (s && s.sku) _stockOverrides[s.sku] = { available: s.available, status: s.status };
			}
		})
		.catch(function () { /* stock is non-critical */ });

	return Promise.all([pricesP, standardP, stockP]);
}

/* ───────────────────────── cart (native storefront) ──────────────────── */

// POST to the storefront's own /ajax/cart/add-item with { Sku, Qty }.
// The storefront converts SKU→ProductId server-side (ConvertSkuService).
function addToCart(sku, qty, name, btn) {
	if (!sku) return;
	// Credit gate — see storefront-credit-guard.js. Degrades to proceeding
	// when the guard script isn't present.
	if (window.PFCreditGuard && window.PFCreditGuard.guard) {
		window.PFCreditGuard.guard({ sku: sku, qty: qty }).then(function (ok) { if (ok) _addToCartNow(sku, qty, name, btn); });
		return;
	}
	_addToCartNow(sku, qty, name, btn);
}
function _addToCartNow(sku, qty, name, btn) {
	if (!sku) return;
	qty = Math.max(1, parseInt(qty, 10) || 1);
	var origin = (window.top && window.top.location) ? window.top.location.origin : window.location.origin;
	var url = origin + '/ajax/cart/add-item';

	var original = btn ? btn.innerHTML : '';
	if (btn) {
		btn.setAttribute('aria-busy', 'true');
		btn.style.pointerEvents = 'none';
		btn.innerHTML = '<i class="fa fa-circle-o-notch fa-spin"></i> Adding';
	}
	var restore = function () {
		if (!btn) return;
		btn.setAttribute('aria-busy', 'false');
		btn.style.pointerEvents = '';
		btn.innerHTML = original;
	};

	// GW-407: stamp the active vehicle filter onto the line's `Data` so it lands
	// on the order line item. Empty string when no vehicle is selected.
	var payload = { Sku: sku, Qty: qty };
	var vehData = vehicleCartData();
	if (vehData) payload.Data = vehData;

	fetch(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
		body: JSON.stringify(payload),
		credentials: 'include'
	})
		.then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
		.then(function (result) {
			restore();
			var success = result && result.data && (result.data.Success === true || result.data.success === true);
			var win = window; try { if (window.top) win = window.top; } catch (e) {}
			var toastr = win.Utils && typeof win.Utils.Toastr === 'function' ? win.Utils.Toastr : null;
			if (result && result.ok && success) {
				if (win.Cart && win.Cart.MiniCart && typeof win.Cart.MiniCart.GetMiniCart === 'function') win.Cart.MiniCart.GetMiniCart();
				if (win.Cart && typeof win.Cart.UpdateCartCount === 'function') win.Cart.UpdateCartCount();
				if (toastr) toastr(addedToCartToast(name, qty));
					// Do NOT auto-open the mini-cart drawer on add (client request): just
					// show the toast + refresh the count. The shopper opens the cart via
					// the toast or the header cart icon when they're ready.
			} else {
				var msg = (result && result.data && (result.data.Message || result.data.message)) || 'Could not add to cart. Please try again.';
				var warnings = result && result.data && (result.data.Warnings || result.data.warnings);
				if (warnings && warnings.length) {
					var texts = warnings.map(function (w) { return w.Message || w.message; }).filter(Boolean);
					if (texts.length) msg += ' ' + texts.join(' ');
				}
				if (toastr) toastr(addFailedToast(msg));
				else if (window.alert) window.alert(msg);
			}
		})
		.catch(function (err) {
			restore();
			console.warn('ProductPrice: add to cart failed', err);
			var win = window; try { if (window.top) win = window.top; } catch (e) {}
			var toastr = win.Utils && typeof win.Utils.Toastr === 'function' ? win.Utils.Toastr : null;
			if (toastr) toastr(addFailedToast('Could not add to cart. Please try again.'));
			else if (window.alert) window.alert('Could not add to cart. Please try again.');
		});
}

/* ──────────────────────── harvest + render the block ─────────────────── */

// Resolve productId / name / sku. The mount element's OWN attributes win
// (the drop-in <div data-sku data-product-id data-name>); otherwise fall
// back to descendants that carry them (when replacing the native block).
function harvestNative(target) {
	var out = {
		productId: target.getAttribute('data-product-id') || '',
		name: target.getAttribute('data-name') || '',
		sku: (target.getAttribute('data-sku') || target.getAttribute('data-product-sku') || '').trim(),
	};
	if (!out.productId) {
		var withId = target.querySelector('[data-product-id]');
		if (withId) {
			out.productId = withId.getAttribute('data-product-id') || '';
			if (!out.name) out.name = withId.getAttribute('data-name') || '';
		}
	}
	if (!out.sku) {
		var withSku = target.querySelector('[data-product-sku]') || target.querySelector('[data-sku]');
		if (withSku) out.sku = (withSku.getAttribute('data-product-sku') || withSku.getAttribute('data-sku') || '').trim();
	}
	return out;
}

// GW-445: a product whose validComm (partstatuslabel) marks it "coming soon" has
// no price/stock yet and must render WITHOUT price, stock, or add-to-cart — just
// the brand + a "Coming Soon" badge. Matched by a case-insensitive substring.
function isComingSoon(part) {
	var label = String((part && part.validComm) || '').toLowerCase();
	return !!label && label.indexOf('coming soon') !== -1;
}

function buildPriceStockHtml(part) {
	var comingSoon = isComingSoon(part);
	var priceNum = _resolvePrice(part);
	var hasPrice = priceNum > 0;
	var reducedFromExcl = _resolveReducedFrom(part);
	var isPromo = hasPrice && reducedFromExcl > priceNum;
	var standardExcl = _resolveStandardPrice(part);
	var showComparison = (_features.showPriceComparison === true) && standardExcl > 0 && standardExcl !== priceNum;

	var stock = _resolveStock(part);
	var isIn = stock.status === 'in_stock';
	var stockText = isIn ? 'In Stock' : 'Out of Stock';

	var h = '';

	// Brand logo + name — shown above the price if the part has a brand (GW-395).
	// The name always renders as a label; the logo (when available) sits beside
	// it so every brand is identified consistently even when a logo is missing,
	// tiny, or an unrecognisable mark.
	if (part.brand) {
		var brandImgUrl = part.manufacturerImage || part.brandImageUrl || '';
		h += '<div class="pfpd-brand">';
		h += '<a href="/parts-finder?page=1&pageSize=25&sort=relevance&brand=' + encodeURIComponent(part.brand) + '" tabindex="0">';
		if (brandImgUrl) {
			// Logo present → show ONLY the logo (the alt still carries the brand name
			// for a11y). The text name is kept as a fallback for brands with no logo,
			// so the brand is never left unlabelled.
			h += '<img src="' + esc(brandImgUrl) + '" alt="' + esc(part.brand) + '" loading="lazy">';
		} else {
			h += '<span class="pfpd-brand-name">' + esc(part.brand) + '</span>';
		}
		h += '</a></div>';
	}

	// Product status / ValidComm (e.g. Discontinued, Selling Out). Role-gated
	// — Supplier/Branch/HO by default (showValidComm). Always shown for a
	// coming-soon product (it IS the "Coming Soon" badge), regardless of the flag.
	if ((comingSoon || _features.showValidComm !== false) && part.validComm) {
		h += '<span class="pfpd-validcomm' + (comingSoon ? ' pfpd-coming-soon' : '') + '">' + esc(part.validComm) + '</span>';
	}

	// Coming soon → no price, no stock, no add-to-cart. Stop after the badge.
	if (comingSoon) {
		h += '<span class="pfpd-coming-soon-note">Newly listed product. Price will be updated upon stock arrival.</span>';
		return h;
	}

	if (_features.showPrices !== false) {
		if (hasPrice) {
			// formatPriceIncl / formatPriceExcl take the excl-VAT amount and apply
			// the 15% VAT + rand formatting (shared helper — replaces the old
			// private formatMoney + VAT_MULTIPLIER math). All positive here (guarded
			// by hasPrice / isPromo / showComparison), so they never return null.
			// Presentation Mode: tag the price number nodes with both the trade and
			// retail formatted values so shared/presentation.js can swap the visible
			// text instantly for Branch/B2B/HO users. Retail attributes are only
			// added when a retail price is actually available (>0) — the swap engine
			// never blanks a price. The promo "was"/badge and the Retail comparison
			// line carry `pf-retail-line` so they're hidden while presenting retail.
			var inclTrade = formatPriceIncl(priceNum);
			var exclTrade = formatPriceExcl(priceNum);
			var retailIncl = standardExcl > 0 ? formatPriceIncl(standardExcl) : '';
			var retailExcl = standardExcl > 0 ? formatPriceExcl(standardExcl) : '';
			var inclAttr = retailIncl ? (' data-pf-price-trade="' + esc(inclTrade) + '" data-pf-price-retail="' + esc(retailIncl) + '"') : '';
			var exclAttr = retailExcl ? (' data-pf-price-trade="' + esc(exclTrade) + '" data-pf-price-retail="' + esc(retailExcl) + '"') : '';
			if (showComparison) {
				// Price comparison: the normal price block (pfpd-price-incl /
				// pfpd-price-excl) with the RETAIL amounts, and the customer's own
				// price on a small grey "Workshop:" line below it (span.pfpd-retail
				// .pfpd-workshop: the old "Retail:" line's theme class + matching
				// inline styles, so no theme change is needed). The retail nodes carry
				// no data-pf-price-* attributes (already retail); the workshop line
				// carries pf-retail-line so Presentation Mode hides it.
				var cmpIncl = _features.showPriceInclVat !== false;
				var cmpExcl = _features.showPriceExclVat !== false;
				if (cmpIncl) {
					h += '<span class="pfpd-price-incl"><span class="pfpd-num">' + retailIncl + '</span> <small>Incl. VAT</small></span>';
				}
				if (cmpExcl) {
					h += '<span class="pfpd-price-excl product-detail-exc-vat">'
						+ '<span class="product-detail-current-price">' + retailExcl + '</span> <small>Excl. VAT</small></span>';
				}
				h += workshopLineHtml({ incl: cmpIncl ? inclTrade : null, excl: cmpExcl ? exclTrade : null, wasIncl: isPromo ? formatPriceIncl(reducedFromExcl) : null, isPromo: isPromo, tag: 'span', className: 'pfpd-retail pfpd-workshop', style: 'font-size:11px;color:#6b7280;margin-top:4px;', taxStyle: 'color:#9ca3af;' });
			} else {
				if (_features.showPriceInclVat !== false) {
					h += '<span class="pfpd-price-incl"><span class="pfpd-num"' + inclAttr + '>' + inclTrade + '</span> <small>Incl. VAT</small>';
					if (isPromo) h += ' <span class="pfpd-was pf-retail-line" title="Regular price">' + formatPriceIncl(reducedFromExcl) + '</span>';
					if (isPromo) h += ' <span class="pfpd-promo pf-retail-line">ON PROMO</span>';
					h += '</span>';
				}
				if (_features.showPriceExclVat !== false) {
					h += '<span class="pfpd-price-excl product-detail-exc-vat">'
						+ '<span class="product-detail-current-price"' + exclAttr + '>' + exclTrade + '</span> <small>Excl. VAT</small></span>';
				}
			}
		} else {
			h += '<span class="pfpd-por">Price on request</span>';
		}
	} else if (isAnonymous()) {
		h += _loginPromptHtml();
	}

	if (_features.showStockStatus !== false) {
		h += '<span class="pfpd-stock-badge ' + (isIn ? 'in' : 'out') + '">' + esc(stockText) + '</span>';
	}

	return h;
}

function _normBranch(s) { return String(s || '').toLowerCase().replace(/[\s_-]/g, ''); }

// The storefront's branch selector owns the selection via `SelectedBranchName`
// (removed when nothing is selected). The legacy `SelectedBranch` /
// `SelectedBranchCode` keys are NEVER written by the storefront — a stale value
// there made a "no branch"/wrong-branch state look selected and showed the wrong
// branch's stock. Read the name only; empty name = no branch selected.
function _selectedBranch() {
	var name = '';
	try {
		name = (window.top || window).localStorage.getItem('SelectedBranchName') || '';
	} catch (e) {
		try { name = localStorage.getItem('SelectedBranchName') || ''; } catch (e2) { /* ignore */ }
	}
	return { name: name.trim(), code: '' };
}

// Prompt shown in place of the branch-stock breakdown when we shouldn't show
// stock (no branch selected, or the selected branch has no credit). The
// button opens the storefront's own branch selector (see openBranchSelector).
function buildSelectBranchPromptHtml(label) {
	var text = label || 'Please select your branch first';
	return '<div class="pfpd-branch pfpd-branch-prompt">'
		+ '<button type="button" class="btn pfpd-select-branch-btn">'
		+ '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
		+ '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>'
		+ '<span>' + esc(text) + '</span>'
		+ '</button>'
		+ '</div>';
}

// Compact per-branch breakdown: the current selected branch + head office (as
// the native product-stock-by-location block showed), rendered as a table.
// warehouse_stock keys are short codes/names resolved via the branch list.
function buildBranchTableHtml(part, branchList, creditCtx) {
	// No branch selected → don't show per-branch stock; prompt the user to
	// choose a branch first (button opens the storefront branch selector).
	var selPre = _selectedBranch();
	if (!selPre.name && !selPre.code) {
		return buildSelectBranchPromptHtml('Please select your branch first');
	}

	// A branch IS selected, but the visitor is a gated account user without
	// credit at it → don't show its stock; prompt them to switch to a branch
	// where they DO have credit (named, e.g. "Please select your branch (Strand)").
	if (creditCtx && creditCtx.gated && !creditCtx.hasCreditHere) {
		var names = (creditCtx.creditBranches || [])
			.map(function (b) { return (b && (b.name || b.code)) || ''; })
			.filter(Boolean);
		var label = names.length
			? 'Please select your branch (' + names.join(', ') + ')'
			: 'Please select a branch where you have credit';
		return buildSelectBranchPromptHtml(label);
	}

	var ws = (part && part.warehouse_stock && typeof part.warehouse_stock === 'object') ? part.warehouse_stock : {};

	// code/name (upper) → display name
	var branchMap = {};
	(branchList || []).forEach(function (b) {
		var name = (b.branchName || b.name || b.branchCode || b.branchShortCode || '').trim();
		if (!name) return;
		var code = (b.branchShortCode || b.branchCode || '').trim().toUpperCase();
		if (code) branchMap[code] = name;
		var nk = name.toUpperCase();
		if (!branchMap[nk]) branchMap[nk] = name;
	});
	function displayName(key) { return branchMap[String(key).toUpperCase()] || key; }

	var stockByUpper = {};
	Object.keys(ws).forEach(function (k) { stockByUpper[k.toUpperCase()] = ws[k]; });
	function qtyFor(key) {
		var q = stockByUpper[String(key).toUpperCase()];
		return (typeof q === 'number' && isFinite(q)) ? q : 0;
	}

	// Candidate keys: every branch we know of, unioned with warehouse_stock keys.
	// Head Office is an internal warehouse — never list it customer-facing, so
	// it's dropped here (covers both the branch list and warehouse_stock keys).
	var keys = [];
	var seen = {};
	function push(k) {
		if (!k) return;
		if (_normBranch(k) === 'headoffice' || _normBranch(k) === 'hq') return;
		if (_normBranch(displayName(k)) === 'headoffice' || _normBranch(displayName(k)) === 'hq') return;
		var u = k.toUpperCase();
		if (!seen[u]) { seen[u] = true; keys.push(k); }
	}
	(branchList || []).forEach(function (b) { push((b.branchShortCode || b.branchCode || b.branchName || b.name || '').trim()); });
	Object.keys(ws).forEach(function (k) { push(k); });
	if (keys.length === 0) return '';

	// Selected branch
	var sel = _selectedBranch();
	var selKey = null;
	if (sel.name || sel.code) {
		keys.forEach(function (k) {
			if (selKey) return;
			var ku = k.toUpperCase();
			if ((sel.code && ku === sel.code.toUpperCase())
				|| (sel.name && (displayName(k).toLowerCase() === sel.name.toLowerCase() || k.toLowerCase() === sel.name.toLowerCase()))) selKey = k;
		});
	}

	// Head Office isn't shown as its own row, but its stock is centrally
	// available, so roll its count into the selected ("your") branch below.
	var headOfficeQty = 0;
	Object.keys(ws).forEach(function (k) {
		if (_normBranch(k) === 'headoffice' || _normBranch(k) === 'hq'
			|| _normBranch(displayName(k)) === 'headoffice' || _normBranch(displayName(k)) === 'hq') {
			var hq = stockByUpper[k.toUpperCase()];
			headOfficeQty += (typeof hq === 'number' && isFinite(hq)) ? hq : 0;
		}
	});

	// Show the selected branch's stock (fallback: all branches — Head Office already excluded).
	var rows = selKey ? [selKey] : keys;

	// Branch (staff) users get Head Office broken out as its OWN row and always
	// see status-only (no counts) — so the selected branch row shows just its own
	// on-hand rather than rolling Head Office into it (that would double-count the
	// indication now that HO is listed separately below).
	var branchUser = isBranch();

	// The link jumps to the native "Branch On Hand" tab (all-branch counts) — which
	// is branch-accounts-only, so drop the link when that feature is off.
	var link = (_features.showBranchStock !== false)
		? '<a class="pfpd-branch-link" href="#pboh-branchonhand">Branch Stock Availability</a>'
		: '';

	var h = '<div class="pfpd-branch">';
	h += link;
	h += '<table class="pfpd-branch-table"><tbody>';
	rows.forEach(function (key) {
		var isSel = selKey && key.toUpperCase() === selKey.toUpperCase();
		// Selected ("your") branch shows its own stock + Head Office stock —
		// EXCEPT for branch users, where Head Office is its own row below.
		var qty = qtyFor(key) + ((isSel && !branchUser) ? headOfficeQty : 0);
		var dn = displayName(key);
		var label = esc(dn);
		if (isSel) label += ' <span class="pfpd-branch-tag">Your branch</span>';
		// showStockQuantity gates the numeric count; branch users are forced to
		// status-only. When counts are off, show in/out status only.
		var showQ = _features.showStockQuantity !== false && !branchUser;
		var qtyText = showQ ? (qty > 0 ? (qty + ' available') : '0 available') : (qty > 0 ? 'In Stock' : 'Out of Stock');
		h += '<tr class="' + (qty > 0 ? 'in' : 'out') + '">'
			+ '<td class="pfpd-branch-name">' + label + '</td>'
			+ '<td class="pfpd-branch-qty">' + qtyText + '</td>'
			+ '</tr>';
	});
	// Head Office row — branch users only. Status-only, so a branch user can see
	// whether the central warehouse can back-fill their branch.
	if (branchUser) {
		var hoIn = headOfficeQty > 0;
		h += '<tr class="' + (hoIn ? 'in' : 'out') + '">'
			+ '<td class="pfpd-branch-name">Head Office <span class="pfpd-branch-tag head">Backup</span></td>'
			+ '<td class="pfpd-branch-qty">' + (hoIn ? 'In Stock' : 'Out of Stock') + '</td>'
			+ '</tr>';
	}
	h += '</tbody></table></div>';
	return h;
}

function buildCartControlsHtml(ctx, comingSoon) {
	// `pfpd-controls` marks THIS (our widget's) controls wrapper so the coming-soon
	// CSS that hides the NATIVE `.product-grid-section` doesn't also hide ours.
	var h = '<div class="product-grid-section pfpd-controls">';
	// Coming-soon products can't be priced or purchased yet — no quantity picker or
	// Add-to-Cart — but Wishlist + Compare (below) still apply.
	if (!comingSoon) {
		h += '<div class="product-detail-quantity"><div class="quantity-controls">'
			+ '<button type="button" class="btn input-decrement pfpd-qty-dec"><i class="fa fa-minus"></i></button>'
			+ '<input type="number" pattern="^[1-9]\\d*$" class="form-control pfpd-qty-input" value="1" min="1">'
			+ '<button type="button" class="btn input-increment pfpd-qty-inc"><i class="fa fa-plus"></i></button>'
			+ '</div></div>';

		if (_features.allowAddToCart !== false) {
			h += '<div><a href="javascript:void(0)" class="btn btn-add-cart pfpd-add-cart"'
				+ ' data-product-id="' + esc(ctx.productId) + '" data-sku="' + esc(ctx.sku) + '" data-name="' + esc(ctx.name) + '">'
				+ '<i class="fa fa-plus"></i>Add to Cart</a></div>';
		}
	}

	// Wishlist + Compare reproduce the native markup/attributes. Compare relies on
	// the storefront's delegated handler; wishlist is wired by wireControls()
	// because the storefront binds it DIRECTLY at page init and so never sees this
	// re-rendered button (no Parts Finder API exists for either).
	//
	// They're wrapped in `.pfpd-secondary-actions` and given a matching labelled
	// treatment (heart + "Add to Wishlist" text) so they stack into a consistent
	// pair — the bare icon-only heart looked lost next to the labelled Compare
	// toggle, especially on mobile.
	h += '<div class="pfpd-secondary-actions">';
	h += '<a href="javascript:void(0)" add-to-wishlist="true" class="btn btn-wishlist pfpd-wishlist"'
		+ ' data-product-id="' + esc(ctx.productId) + '" data-name="' + esc(ctx.name) + '"'
		+ ' sf-ga-trigger="click" sf-ga-event="add_to_wishlist"'
		+ ' sf-ga-data-endpoint="/ajax/google-analytics-4/view/item?productId=' + esc(ctx.productId) + '">'
		+ '<span class="material-symbols-outlined" aria-hidden="true">favorite</span>'
		+ '<span class="pfpd-action-label">Add to Wishlist</span></a>';

	h += '<div class="product-box-compare pfpd-compare">'
		+ '<input type="checkbox" id="cb_' + esc(ctx.sku) + '" name="Compare" data-selector="' + esc(ctx.sku) + '" data-product-sku="' + esc(ctx.sku) + '">'
		+ '<label for="cb_' + esc(ctx.sku) + '"><span class="toggle-track"></span>Add to Compare</label>'
		+ '</div>';
	h += '</div>';

	h += '</div>';
	return h;
}

// A layout-matching skeleton shown the moment the mount is found, so there's
// no empty gap while features / price / stock resolve. render() replaces it.
function skel(w, h, mt) {
	return '<div class="pfpd-skel" style="width:' + w + ';height:' + h + 'px;' + (mt ? 'margin-top:' + mt + 'px;' : '') + '"></div>';
}
function renderSkeleton(target) {
	var h = '';
	h += '<div class="product-detail-price pfpd-price pfpd-loading" aria-busy="true">';
	h += skel('100px', 22);      // brand logo
	h += skel('58%', 26, 10);    // incl. VAT price
	h += skel('38%', 13, 8);     // excl. VAT price
	h += skel('92px', 22, 12);   // stock badge
	h += '<div class="pfpd-branch" style="margin-top:14px;">';
	h += skel('45%', 12);        // "Branch Stock Availability" link
	h += '<div class="pfpd-branch-table">';
	for (var i = 0; i < 2; i++) {
		h += '<div class="pfpd-skel-row">'
			+ '<div class="pfpd-skel" style="width:42%;height:13px;"></div>'
			+ '<div class="pfpd-skel" style="width:72px;height:13px;"></div>'
			+ '</div>';
	}
	h += '</div></div>';
	h += '</div>';
	h += '<div class="product-grid-section pfpd-loading" style="margin-top:14px;display:flex;gap:10px;align-items:center;">';
	h += skel('108px', 40);      // quantity control
	h += skel('150px', 40);      // add-to-cart button
	h += '</div>';
	target.innerHTML = h;
}

// GW-445: the OE product-detail theme renders its OWN buy-box (price, branch
// stock, add-to-cart, compare) as a sibling of our widget inside
// `.product-detail-information`. Our widget hides ITS price/cart for a coming-soon
// product, but that native buy-box still shows R0.00 + Add to Cart. OE has no
// coming-soon flag server-side, so we hide the native buy-box from here when we
// know the product is coming soon. Hides the whole native wrapper (covers the
// class-less branch-stock block); a CSS backup (.pfpd-coming-soon-page) keeps
// price/cart hidden if OE re-renders. Best-effort — never throws.
function hideNativeBuyBox(target) {
	try {
		var info = (target && target.closest) ? target.closest('.product-detail-information') : null;
		if (!info) info = getTopDocument().querySelector('.product-detail-information');
		if (!info) return;
		// CSS backup hook.
		if (info.className.indexOf('pfpd-coming-soon-page') === -1) {
			info.className = (info.className + ' pfpd-coming-soon-page').trim();
		}
		// The native price panel is a `.product-detail-price` WITHOUT our `pfpd-price`
		// marker. Hide the native buy-box wrapper that holds it (the direct child of
		// `.product-detail-information` — contains price + branch stock + add-to-cart
		// + compare), leaving our own widget (`#pf-product-price` / [data-parts-…])
		// untouched.
		var prices = info.querySelectorAll('.product-detail-price');
		for (var i = 0; i < prices.length; i++) {
			var p = prices[i];
			if (p.className.indexOf('pfpd-price') !== -1) continue; // our own panel
			var wrap = p;
			while (wrap && wrap.parentNode !== info) wrap = wrap.parentNode;
			if (wrap && wrap !== target && wrap.id !== 'pf-product-price' && !wrap.hasAttribute('data-parts-product-price')) {
				wrap.style.display = 'none';
			}
		}
	} catch (e) { /* non-fatal */ }
}

// GW-445: coming-soon products have no photo yet, so the GW _Gallery.cshtml
// override renders its generic "no image" PNG as an unsized background (only the
// top-left corner shows). Swap that placeholder for the "Image coming soon" SVG
// (served from the parts-finder app) and centre it. Only placeholder nodes are
// touched, so a coming-soon product that does have a real photo keeps it.
// Best-effort, never throws.
function useComingSoonGalleryImage() {
	try {
		var doc = getTopDocument();
		var src = getAppBaseUrl() + 'images/image-coming-soon.svg';
		var NEEDLE = 'no-image';
		var nodes = doc.querySelectorAll('.product-detail-screen [style*="' + NEEDLE + '"], .product-detail-screen img[src*="' + NEEDLE + '"], .product-detail-screen a[href*="' + NEEDLE + '"]');
		for (var i = 0; i < nodes.length; i++) {
			var el = nodes[i];
			if (el.tagName === 'IMG') {
				el.src = src;
				el.alt = 'Image coming soon';
			} else if (el.tagName === 'A') {
				el.href = src;
			} else {
				el.style.backgroundImage = "url('" + src + "')";
				el.style.backgroundSize = 'contain';
				el.style.backgroundPosition = 'center';
				el.style.backgroundRepeat = 'no-repeat';
				if (el.hasAttribute('data-href')) el.setAttribute('data-href', src);
				el.title = 'Image coming soon';
			}
		}
	} catch (e) { /* non-fatal */ }
}

// GW: per-branch stock QUANTITIES are reserved for branch accounts. The native OE
// product page renders them (".item-in-stock" heading + ".product-stock-by-location"
// per-branch count list, plus the "Branch On Hand" tab that lists every branch's
// count) with no role awareness — so B2B/B2C/Public saw exact counts + Head Office.
// When the role's `showBranchStock` feature is off we flag <html> (CSS hides those
// surfaces) and hide the tab's nav button here. Our own widget's branch view
// (.pfpd-branch-*) is untouched — it already shows status, not counts, to these roles.
function hideNativeBranchStock() {
	try {
		var doc = getTopDocument();
		var root = doc.documentElement;
		if (root && root.className.indexOf('pfpd-no-branch-stock') === -1) {
			root.className = (root.className + ' pfpd-no-branch-stock').trim();
		}
		// Hide the Branch-On-Hand tab's NAV button (the pane itself is hidden by CSS).
		var navs = doc.querySelectorAll('a[href="#pboh-branchonhand"],[data-target="#pboh-branchonhand"],[data-bs-target="#pboh-branchonhand"]');
		for (var i = 0; i < navs.length; i++) {
			var a = navs[i];
			// Leave our own widget's link alone (it's removed separately when the
			// feature is off — see buildBranchTableHtml).
			if (a.closest && (a.closest('#pf-product-price') || a.closest('[data-parts-product-price]'))) continue;
			var li = a.closest ? a.closest('li') : null;
			(li || a).style.display = 'none';
		}
	} catch (e) { /* non-fatal */ }
}

function render(target, part, ctx, branchList, creditCtx) {
	// Preserve native sub-nodes we must not destroy. The payment widgets
	// container is always kept; the native branch nodes are kept only as a
	// fallback when we can't build our own data-driven branch table.
	var preservedPayment = target.querySelector('.payment-widgets-container');
	var preservedBranchList = target.querySelector('.product-stock-by-location');
	var preservedBranchLink = target.querySelector('.item-in-stock');
	if (preservedPayment && preservedPayment.parentNode) preservedPayment.parentNode.removeChild(preservedPayment);
	if (preservedBranchList && preservedBranchList.parentNode) preservedBranchList.parentNode.removeChild(preservedBranchList);
	if (preservedBranchLink && preservedBranchLink.parentNode) preservedBranchLink.parentNode.removeChild(preservedBranchLink);

	var doc = getTopDocument();

	// GW-445: coming-soon products render brand + "Coming Soon" only — no branch
	// stock table and no cart controls (below).
	var comingSoon = isComingSoon(part);
	// Also strip the NATIVE OE buy-box (price / stock / add-to-cart) that renders
	// alongside our widget — OE has no coming-soon flag, so we hide it here.
	if (comingSoon) hideNativeBuyBox(target);
	// ...and replace the generic no-image placeholder with the coming-soon image.
	if (comingSoon) useComingSoonGalleryImage();
	// Per-branch stock QUANTITIES are branch-accounts-only — hide the native OE
	// count surfaces for roles without showBranchStock.
	if (_features.showBranchStock === false) hideNativeBranchStock();

	// Price + stock panel (native classes so storefront CSS applies).
	var priceWrap = doc.createElement('div');
	priceWrap.className = 'product-detail-price pfpd-price';
	var branchHtml = (!comingSoon && _features.showStockStatus !== false) ? buildBranchTableHtml(part, branchList, creditCtx) : '';
	priceWrap.innerHTML = buildPriceStockHtml(part) + branchHtml;
	// Fallback: if we couldn't build a branch table from data, keep the
	// native branch link + per-location list (legacy [sftarget] block).
	if (!branchHtml) {
		if (preservedBranchLink) priceWrap.appendChild(preservedBranchLink);
		if (preservedBranchList) priceWrap.appendChild(preservedBranchList);
	}

	// Rebuild the target.
	target.innerHTML = '';
	target.appendChild(priceWrap);
	if (preservedPayment) {
		target.appendChild(preservedPayment);
	} else {
		var pw = doc.createElement('div');
		pw.className = 'payment-widgets-container';
		pw.style.cssText = 'display:flow-root;clear:both';
		target.appendChild(pw);
	}
	// Cart controls. Coming soon → buildCartControlsHtml omits quantity/add-to-cart
	// but still renders Wishlist + Compare (a shopper can save/compare a product
	// that isn't buyable yet). wireControls is null-safe for the absent qty/cart.
	var controls = doc.createElement('div');
	controls.innerHTML = buildCartControlsHtml(ctx, comingSoon);
	while (controls.firstChild) target.appendChild(controls.firstChild);
	wireControls(target, ctx);
}

// Open the storefront's own branch selector. Prefers the documented API
// (window.SFUI.branchSelector.openDrawer, exposed by storefront.branch-
// selector.js), falling back to clicking the #branch-selector-btn trigger.
// Runs against the TOP window since the widget is usually iframed.
function openBranchSelector() {
	var win = window;
	try { if (window.top) win = window.top; } catch (e) { /* cross-origin — use current */ }
	try {
		if (win.SFUI && win.SFUI.branchSelector && typeof win.SFUI.branchSelector.openDrawer === 'function') {
			win.SFUI.branchSelector.openDrawer();
			return;
		}
	} catch (e) { /* fall through */ }
	try {
		var btn = getTopDocument().getElementById('fulfilment-selector-btn') || getTopDocument().getElementById('branch-selector-btn');
		if (btn) { btn.click(); return; }
	} catch (e) { /* ignore */ }
	console.warn('ProductPrice: branch selector not found (window.SFUI.branchSelector / #branch-selector-btn).');
}

// Open the storefront's "add to wishlist" modal for this product.
//
// We re-render the wishlist button (buildCartControlsHtml), but the storefront
// binds its handler with a DIRECT `Utils.Bind($('[add-to-wishlist]'),'click',…)`
// at page init (product-detail.js) — which only ever sees the ORIGINAL button we
// then replace. So the click is dead unless we wire it ourselves. This mirrors
// the storefront's own open flow: load the visitor's wishlists via the SDK,
// populate #Wishlist, then show #add-wishlist-modal with the product set. The
// modal's own "Add to wishlist" confirm button is still handled by the
// storefront (we don't touch the modal). No-ops gracefully if the SDK/modal
// aren't on the page.
function openWishlist(ctx, btn) {
	var win = window; try { if (window.top) win = window.top; } catch (e) { /* ignore */ }
	var $ = win.jQuery || win.$ || window.jQuery || window.$;
	var sdk = win.sdk || window.sdk;
	var Utils = win.Utils || window.Utils;
	if (!$ || !sdk || !sdk.wishlist || typeof sdk.wishlist.get !== 'function') return;
	if (!$('#add-wishlist-modal').length) return;
	if (btn) btn.setAttribute('aria-busy', 'true');
	sdk.wishlist.get({
		success: function (response) {
			if (btn) btn.setAttribute('aria-busy', 'false');
			var items = response && response.data;
			if (!items) {
				if (Utils && typeof Utils.Toastr === 'function') {
					Utils.Toastr({ style: 'warning', message: 'You need to be logged in to add items to a wishlist.' });
				}
				return;
			}
			var $sel = $('#Wishlist');
			$sel.empty();
			$sel.append('<option selected data-default="true" disabled="disabled">Select or add a new wishlist.</option>');
			for (var i = 0; i < items.length; i++) {
				var it = items[i] || {};
				$sel.append('<option data-name="' + it.Name + '" value="' + it.DocumentId + '">' + it.Name + '</option>');
			}
			var $modal = $('#add-wishlist-modal');
			$modal.data('product-id', ctx.productId);
			$modal.data('name', ctx.name);
			$modal.modal('show');
		},
		error: function (resp) {
			if (btn) btn.setAttribute('aria-busy', 'false');
			try { console.log('Parts Finder: wishlist load failed', resp); } catch (e) { /* ignore */ }
		}
	});
}

function wireControls(target, ctx) {
	var branchBtn = target.querySelector('.pfpd-select-branch-btn');
	if (branchBtn) {
		branchBtn.addEventListener('click', function (e) {
			e.preventDefault();
			openBranchSelector();
		});
	}

	var input = target.querySelector('.pfpd-qty-input');
	var readQty = function () { return Math.max(1, parseInt(input && input.value, 10) || 1); };

	var dec = target.querySelector('.pfpd-qty-dec');
	var inc = target.querySelector('.pfpd-qty-inc');
	if (dec) dec.addEventListener('click', function () { if (input) input.value = String(Math.max(1, readQty() - 1)); });
	if (inc) inc.addEventListener('click', function () { if (input) input.value = String(readQty() + 1); });

	var cartBtn = target.querySelector('.pfpd-add-cart');
	if (cartBtn) {
		cartBtn.addEventListener('click', function (e) {
			e.preventDefault();
			addToCart(ctx.sku, readQty(), ctx.name, cartBtn);
		});
	}

	// Wire the wishlist button we re-rendered (the storefront's direct bind never
	// sees it). preventDefault only — we must NOT stop propagation, so the
	// storefront's delegated [sf-ga-trigger] handler still fires add_to_wishlist.
	var wishBtn = target.querySelector('[add-to-wishlist], .btn-wishlist');
	if (wishBtn && !wishBtn.__pfWishWired) {
		wishBtn.__pfWishWired = true;
		wishBtn.addEventListener('click', function (e) {
			e.preventDefault();
			openWishlist(ctx, wishBtn);
		});
	}
}

/* ─────────────────────────────── init ────────────────────────────────── */

// Optional per-instance config carrier. In the bundle there's a single <script>
// tag, so the old <script data-*> config moves onto this element on the page.
function findConfigEl() {
	return getTopDocument().querySelector('[data-parts-product-price]');
}

// Find the mount element: a data-target-selector on [data-parts-product-price]
// if set, else the native live-data block [sftarget="live-data"].
function resolveTarget() {
	var doc = getTopDocument();
	var cfg = findConfigEl();
	var sel = cfg && cfg.getAttribute('data-target-selector');
	if (sel && sel.trim()) return doc.querySelector(sel.trim());
	return doc.querySelector('[sftarget="live-data"]');
}

function getConfiguredSku(cfg, target) {
	// 1. explicit data-sku on the config element
	if (cfg) {
		var ds = cfg.getAttribute('data-sku');
		if (ds && ds.trim()) return ds.trim();
		// 2. data-sku-selector → element with data-sku / value
		var skuSelector = cfg.getAttribute('data-sku-selector');
		if (skuSelector) {
			var el = getTopDocument().querySelector(skuSelector);
			if (el) {
				var v = el.getAttribute('data-sku') || (el.value && el.value.trim()) || el.getAttribute('value');
				if (v && v.trim()) return v.trim();
			}
		}
	}
	// 3. harvest from the block being replaced
	var harvested = harvestNative(target);
	return harvested.sku || '';
}

// Resolve credit AFTER the widget has painted. If the visitor is a gated
// account user without credit at the selected branch, swap the branch-stock
// table for the "select a branch where you have credit" prompt. Fails open
// (leaves stock shown) on any error — never blocks or clears the widget.
function applyCreditGate(target, part, branchList) {
	fetchCreditContext().then(function (creditCtx) {
		// Opt-in diagnostic (append ?pfdebug=1 to the page URL). Shows exactly why
		// the switch-branch prompt did or didn't render for this visitor.
		try {
			if (PF_DEBUG) {
				console.log('[ProductPrice] creditGate decision:', creditCtx ? {
					gated: creditCtx.gated,
					hasCreditHere: creditCtx.hasCreditHere,
					activeCode: creditCtx.activeCode,
					activeName: creditCtx.activeName,
					creditBranches: (creditCtx.creditBranches || []).map(function (b) { return b.code; }),
				} : 'null (PFCreditGuard unavailable)');
			}
		} catch (e) { /* ignore */ }
		if (!creditCtx || !creditCtx.gated || creditCtx.hasCreditHere) return; // stock stays as rendered
		var el = target.querySelector('.pfpd-branch');
		if (!el || !el.parentNode) return;
		var tmp = document.createElement('div');
		tmp.innerHTML = buildBranchTableHtml(part, branchList, creditCtx);
		var replacement = tmp.firstElementChild;
		if (!replacement) return;
		el.parentNode.replaceChild(replacement, el);
		// Re-wire the branch-select button on the swapped-in prompt.
		var btn = replacement.querySelector('.pfpd-select-branch-btn');
		if (btn) btn.addEventListener('click', function (e) { e.preventDefault(); openBranchSelector(); });
	}).catch(function () { /* fail open — leave stock shown */ });
}

function run() {
	var target = resolveTarget();
	if (!target) return; // no-op cleanly when this widget's mount isn't on the page
	if (target.getAttribute('data-pfpd-done') === '1') return; // guard double-run
	target.setAttribute('data-pfpd-done', '1');

	var cfg = findConfigEl();
	var native = harvestNative(target);
	var sku = getConfiguredSku(cfg, target);
	if (!sku) {
		console.warn('ProductPrice: no SKU (set data-sku / data-sku-selector on [data-parts-product-price], or ensure the block has data-product-sku).');
		target.removeAttribute('data-pfpd-done');
		return;
	}
	var ctx = { sku: sku, productId: native.productId, name: native.name };

	// Paint the skeleton immediately so there's no empty gap while data loads.
	renderSkeleton(target);

	// Features, part and branches are independent — fetch them together instead
	// of in sequence. Prices/stock run next because they read the resolved
	// feature flags (e.g. showPriceComparison).
	Promise.all([loadFeatures(), fetchPart(sku), fetchBranches()])
		.then(function (res) {
			_features = res[0] || {};
			var part = res[1] || { sku: sku, price: 0 };
			if (!part.sku) part.sku = sku;
			var branchList = Array.isArray(res[2]) ? res[2] : [];
			_branchListCache = branchList; // enable branch-aware stock in _resolveStock
			if (!ctx.name) ctx.name = part.title || part.name || '';
			return fetchPricesAndStock([sku]).then(function () { return { part: part, branchList: branchList }; });
		})
		.then(function (d) {
			// Paint immediately (credit context is NOT awaited here — see below).
			render(target, d.part, ctx, d.branchList, null);
			// Apply the credit gate to the branch-stock section in the background.
			// The credit lookup (b2b → DMC) can be slow; keeping it off the initial
			// render path means the widget paints fast, then the branch section is
			// swapped to the "no credit here" prompt only if needed.
			applyCreditGate(target, d.part, d.branchList);
		})
		.catch(function (err) {
			console.warn('ProductPrice: render failed', err);
			target.innerHTML = ''; // clear the skeleton so it doesn't shimmer forever
			target.removeAttribute('data-pfpd-done');
		});
}

export function init() {
	// Run now if the mount is already present; run() no-ops cleanly if it isn't.
	run();

	// SPA / late-rendered product pages: watch for the mount appearing. The
	// data-pfpd-done guard on the target keeps our own render()/innerHTML writes
	// from re-triggering run() into a loop.
	try {
		var doc = getTopDocument();
		var observer = new MutationObserver(function () {
			var t = resolveTarget();
			if (t && t.getAttribute('data-pfpd-done') !== '1') run();
		});
		observer.observe(doc.body || doc.documentElement, { childList: true, subtree: true });
	} catch (e) { /* MutationObserver unsupported — single run is fine */ }
}

export var id = 'product-price';
